import { type AgentSummary, type AnalysisDetail, AnalysisDetailAccessMode, AnalysisDetailState } from "Infrastructure/ApiZfp/Schema";
import { useUserContext } from "Infrastructure/Context/UserContext";
import { FeatureFlag, useFeatureFlag } from "Infrastructure/Hooks/useFeatureFlag";

export const AnalysisAccessStatus = {
  editable: "editable",
  sharedWithOther: "shared_with_other",
  sharedWithMe: "shared_with_me",
  declined: "declined",
  completed: "completed",
} as const;

export type AnalysisAccessStatus = (typeof AnalysisAccessStatus)[keyof typeof AnalysisAccessStatus];

export type AnalysisAccess = {
  readonly status: AnalysisAccessStatus;
  readonly isEditable: boolean;
  readonly isOwner: boolean;
  readonly canChangeState: boolean;
  readonly canCreateNewVersion: boolean;
  readonly canAddFamilyMember: boolean;
  readonly isLatestVersion: boolean;
  readonly sharedWithAgent: AgentSummary | null;
};

const isClosedState = (state: AnalysisDetailState) => state === AnalysisDetailState.completed || state === AnalysisDetailState.declined;

// `sharedWithMe` wins over the analysis state only for the status bar, a closed analysis stays read-only (see `isEditable`)
// and the new version is offered through `canCreateNewVersion`
const getStatus = (analysis: AnalysisDetail, hasEditAccess: boolean, isSharedWithMe: boolean): AnalysisAccessStatus => {
  if (!hasEditAccess) return AnalysisAccessStatus.sharedWithOther;
  if (isSharedWithMe) return AnalysisAccessStatus.sharedWithMe;
  if (analysis.state === AnalysisDetailState.completed) return AnalysisAccessStatus.completed;
  if (analysis.state === AnalysisDetailState.declined) return AnalysisAccessStatus.declined;
  return AnalysisAccessStatus.editable;
};

export const useAnalysisAccess = (analysis: AnalysisDetail | undefined): AnalysisAccess => {
  const isAnalysisSharingEnabled = useFeatureFlag(FeatureFlag.AnalysisSharing);
  const { getGuid } = useUserContext();

  // stay read-only until the analysis is known, so nothing can be edited before access is resolved
  if (!analysis) {
    return {
      status: AnalysisAccessStatus.editable,
      isEditable: false,
      isOwner: false,
      canChangeState: false,
      canCreateNewVersion: false,
      canAddFamilyMember: false,
      isLatestVersion: false,
      sharedWithAgent: null,
    };
  }

  const isOwner = analysis.ownerUserGuid === getGuid();
  const sharedWithAgent = analysis.editingAgent && analysis.editingAgent.userGuid !== analysis.ownerUserGuid ? analysis.editingAgent : null;
  // without the flag nothing can be shared, so the requesting agent always holds the edit access
  const hasEditAccess = !isAnalysisSharingEnabled || analysis.accessMode === AnalysisDetailAccessMode.edit;
  const status = getStatus(analysis, hasEditAccess, isAnalysisSharingEnabled && !isOwner);
  // `readOnly` means a newer version of the analysis exists
  const isArchived = analysis.readOnly;

  const isEditable = !isArchived && hasEditAccess && !isClosedState(analysis.state);

  return {
    status,
    isEditable,
    isOwner,
    canChangeState: !isArchived && hasEditAccess && (!isAnalysisSharingEnabled || isOwner),
    canCreateNewVersion: !isArchived && hasEditAccess && isClosedState(analysis.state),
    canAddFamilyMember: isEditable && (!isAnalysisSharingEnabled || isOwner),
    isLatestVersion: !isArchived,
    sharedWithAgent,
  };
};
